import { Spin } from 'antd';
import { get, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { position_finder_not_found } from '../../assets/img';
import { AlertMessage, BreadCrumb } from '../../components';
import { getBreadCrumbList } from '../../utils/helper';
import MenuBar from './MenuBar/MenuBar';
import styles from './position-finder-function.module.scss';
import PositionFilterFinder from './PositionFilterFinder/PositionFilterFinder';
import PositionFinderDetail from './PositionFinderDetail/PositionFinderDetail';
import positionFinderApi from '../../services/positionFinderApi';
import { PERMISSION, SELECT_ALL_LABEL } from '../../utils/constants';
import GlobalAlertMessage from '../../components/GlobalAlertMessage/GlobalAlertMessage';

const limit = 16;

const convertToDisplay = (talentFinderList, currentPage) => {
  if (isEmpty(talentFinderList)) return [];
  const data = [];
  talentFinderList.forEach((d, i) => {
    data.push({
      ...d,
      key: (currentPage - 1) * 16 + 1 + i,
      position_name: d.position_name || '-',
      position_code: d.position_code,
      sector: d.sector || '-',
      opu: d.opu || '-',
      division: d.division || '-',
      department: d.department || '-',
      job_grade: d.job_grade || '-',
      job_function: d.job_function || '-',
      skg: d.skg || '-',
      skg_text: d.skg_text,
      current_incumbent: d.current_incumbent || '-',
      current_incumbent_code: d.current_incumbent_code,
    });
  });
  return data;
};

const PositionFinderFunction = () => {
  const roleActive = useSelector((state) => state.user.roleActive);
  const [showFilter, setShowFilter] = useState(false);
  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [filterOptions, setFilterOptions] = useState({});
  const [cloneFilterOptions, setCloneFilterOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [showError, setShowError] = useState(false);
  const [showExporting, setShowExporting] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [filterFinderData, setFilterFinderData] = useState({});
  const [isFilter, setIsFilter] = useState(false);
  const [keySearch, setKeySearch] = useState('');
  const [debouncedSearchValue, setDebouncedInputValue] = useState({
    search_type: '',
    keyword: '',
  });

  function onPageChange(value) {
    setPage(value);
  }

  function onClear() {
    setData([]);
    setTotalData(0);
  }

  const handleFormatFilterData = (data) => {
    return data?.length > 0 ? data.filter((d) => d.checked && d.label !== SELECT_ALL_LABEL).map((item) => item.value) : [];
  };

  const fetchApiListFilter = useCallback(
    async (filter, page, limit, keyword) => {
      try {
        setLoading(true);
        const tempBody = { ...filter, page, limit, keyword };
        const tempBodyPayload = {
          ...tempBody,
          years_to_retire: handleFormatFilterData(tempBody.years_to_retire),
          years_in_position: handleFormatFilterData(tempBody.years_in_position),
        };
        const res = await positionFinderApi.getPositionDataList(tempBodyPayload, roleActive, PERMISSION.SEARCH_AND_VIEW_POSITION);
        if (res.status === 200) {
          const positionFinderList = get(res, 'data.result.data', []);
          const totalData = get(res, 'data.result.total', 0);
          setTotalData(totalData);
          setData(convertToDisplay(positionFinderList, page));
        }
      } catch (error) {
        console.error(error);
        setData([]);
        setTotalData(0);
      } finally {
        setLoading(false);
      }
    },
    [roleActive]
  );

  useEffect(() => {
    if (!isEmpty(filterFinderData) || isSearch) {
      fetchApiListFilter(filterFinderData, page, limit, keyword);
    } else {
      onClear();
    }
  }, [keyword, isSearch, filterFinderData, page, fetchApiListFilter]);

  useEffect(() => {
    async function fetchApiOptions() {
      try {
        setLoadingFilter(true);
        const filterRes = await positionFinderApi.getPositionFilters({
          search_type: '',
          keyword: '',
        });
        if (filterRes && filterRes.status === 200) {
          setFilterOptions({ ...filterRes.data.result });
          setCloneFilterOptions({ ...filterRes.data.result });
        }
      } catch (error) {
        console.error(error);
        setFilterOptions([]);
        setCloneFilterOptions([]);
      }
      setLoadingFilter(false);
    }
    fetchApiOptions();
  }, []);

  useEffect(() => {
    if (showError === false) return;
    const timeout = setTimeout(() => {
      setShowError(false);
    }, 3000);
    return () => {
      clearTimeout(timeout);
    };
  }, [showError]);
  return (
    <div className={styles.max_width}>
      <div className={styles.control}>
        <BreadCrumb level={2} breadCrumbList={getBreadCrumbList([`Dashboard`, `Position Finder`])} />
        <GlobalAlertMessage style={{ marginBottom: '20px' }} id={'POSITION_FINDER_MODULE'} />
        <div style={{ marginBottom: '20px' }}>
          {showError && (
            <AlertMessage
              message="You need to enter a search term before pressing search"
              type="error"
              onClose={() => {
                setTimeout(() => {
                  setShowError(false);
                }, 300);
              }}
            />
          )}
          <div className={styles.show_exporting} style={showExporting ? { height: '62px' } : { height: '0' }}>
            {showExporting && (
              <AlertMessage
                message="Position Data export request has been received and is being processed. Once completed a download link will be sent to the notification."
                type="success"
                onClose={() => {
                  setTimeout(() => {
                    setShowExporting(false);
                  }, 100);
                }}
              />
            )}
          </div>
        </div>
        <h3 className={styles.title}>Position Finder</h3>
      </div>
      <MenuBar
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        setKeyword={setKeyword}
        keyword={keyword}
        setShowError={setShowError}
        setIsSearch={setIsSearch}
        setPage={setPage}
        onClear={onClear}
        filterFinderData={filterFinderData}
        role={roleActive}
        totalData={totalData}
        setShowExporting={setShowExporting}
        isFilter={isFilter}
        setIsFilter={setIsFilter}
        keySearch={keySearch}
        setKeySearch={setKeySearch}
      />
      {
        <Spin spinning={loadingFilter} size="large">
          <PositionFilterFinder
            filterOptions={filterOptions}
            prevFilterOptions={cloneFilterOptions}
            setFilterFinderData={setFilterFinderData}
            filterFinderData={filterFinderData}
            setIsFilter={setIsFilter}
            keySearch={keySearch}
            setKeyword={setKeyword}
            setPage={setPage}
            showFilter={showFilter}
            setDebouncedInputValue={setDebouncedInputValue}
            debouncedSearchValue={debouncedSearchValue}
            setFilterOptions={setFilterOptions}
          />
        </Spin>
      }
      {!isEmpty(data) ? (
        <Spin spinning={loading} size="large">
          <PositionFinderDetail data={data} page={page} limit={limit} onPageChange={onPageChange} totalData={totalData} />
        </Spin>
      ) : (
        <div className={`${styles.not_found} position-relative`}>
          <div className="center-loading">
            <Spin spinning={loading} size="large"></Spin>
          </div>
          <img src={position_finder_not_found} alt="position_finder_not_found" style={loading ? { opacity: '0.3' } : null} />
        </div>
      )}
    </div>
  );
};

export default PositionFinderFunction;
